import React, { FC, useState, useEffect } from 'react';
import classNames from 'classnames';

import Button from 'vibo-ui/Button';
import LazyImage from './LazyImage';
import Spinner from 'vibo-ui/Spinner';
import GradientCover from 'vibo-ui/GradientCover';
import DefaultImagePlaceholder from './DefaultImagePlaceholder';
import Icon, { IconmoonFont } from 'vibo-ui/Icon';

import { DEFAULT_GRADIENT_ID } from './constants';

import { ImageProps } from './interfaces';

import useStyles from './style';

const Image: FC<ImageProps> = ({
  src,
  className,
  children,
  placeholder,
  style,
  isUpdating,
  label,
  onClick,
  handleDelete,
  isLazy = true,
  isPickable = false,
  loading = 'eager',
  alt = 'image',
  debounceLoadMs = 300,
  gradientId = DEFAULT_GRADIENT_ID,
  ...rest
}) => {
  const classes = useStyles();

  const [isLoadError, setIsLoadError] = useState<boolean>(false);

  const defaultImageProps = {
    src,
    ...rest,
    loading,
    alt,
  };

  const handleLoadError = () => setIsLoadError(true);

  const imagePlaceholder =
    !!placeholder || (isLoadError && !children && !gradientId) ? (
      <DefaultImagePlaceholder>{placeholder}</DefaultImagePlaceholder>
    ) : (
      <GradientCover gradientId={gradientId}>{children}</GradientCover>
    );

  useEffect(() => {
    if (src) {
      setIsLoadError(false);
    } else {
      handleLoadError();
    }
  }, [src]);

  return (
    <div
      className={classNames('viboImage', classes.viboImage, className, {
        isPickable,
        isLoadError,
      })}
      onClick={onClick}
      style={style}
    >
      {isLoadError ? (
        imagePlaceholder
      ) : isLazy ? (
        <LazyImage
          {...defaultImageProps}
          debounceLoadMs={debounceLoadMs}
          setIsLoadError={setIsLoadError}
          handleLoadError={handleLoadError}
        />
      ) : (
        <img {...defaultImageProps} alt={alt} onError={handleLoadError} />
      )}
      {isUpdating ? (
        <Spinner className="cornerIcon" />
      ) : isPickable ? (
        <Icon icon={IconmoonFont['camera-24']} className="cornerIcon" />
      ) : null}
      {!!handleDelete ? (
        <Button
          onClick={handleDelete}
          prefixIcon={IconmoonFont['delete-16']}
          className={classNames('removeImageBtn', classes.removeImageBtn)}
          shape="round"
        />
      ) : null}
      {label}
    </div>
  );
};

export default Image;
